import React, { useMemo, useState, useEffect } from "react";
import { CatBox, InteractiveCatBox } from "./CatBox";
import { SHOW_DEBUG } from "../config";
import { CategoryData } from "../config/types";
import {
  buildCoordinatePlaneBounds,
  CoordinatePlaneBounds,
} from "../utils/grid";
import { Grid, Typography } from "@mui/material";
import { BudgetPercentages } from "../utils/budgetCalculations";

interface CoordinatePlaneGridProps {
  categories: CategoryData[];
  adultMode: boolean;
  screenWidth: number;
  screenHeight: number;
  selectedCategoryIndex?: number;
  transferAnimation?: {
    from: number;
    to: number;
  };
  onCategoryClick?: (index: number) => void;
  /** optional live center positions coming from Phaser – overrides grid */
  catPositions?: { x: number; y: number }[];
  /** top margin (px) reserved for UI above grid */
  topMargin?: number;
  /** level precision for X-axis scaling */
  levelPrecision?: number;
  /** fixed bounds for the coordinate plane (prevents shifting during gameplay) */
  fixedBounds?: { minSurplus: number; maxSurplus: number };
  /** indices of cats that should be hidden (e.g., disappeared into portal) */
  hiddenCatIndices?: Set<number>;
  /** budget percentages for Needs, Wants, and Savings */
  budgetPercentages?: BudgetPercentages;
}

export const CoordinatePlaneGrid: React.FC<CoordinatePlaneGridProps> = ({
  categories,
  adultMode,
  screenWidth,
  screenHeight,
  selectedCategoryIndex,
  transferAnimation,
  onCategoryClick,
  catPositions,
  topMargin = 0,
  levelPrecision = 100,
  fixedBounds,
  hiddenCatIndices,
  budgetPercentages,
}) => {
  const [portalOpen, setPortalOpen] = useState(false);

  useEffect(() => {
    const open = () => setPortalOpen(true);
    const close = () => setPortalOpen(false);
    window.addEventListener("phaser-portal-open", open);
    window.addEventListener("phaser-portal-closed", close);
    return () => {
      window.removeEventListener("phaser-portal-open", open);
      window.removeEventListener("phaser-portal-closed", close);
    };
  }, []);

  // Calculate coordinate plane layout
  const coordinateBounds = useMemo(() => {
    const bounds = buildCoordinatePlaneBounds(
      screenWidth,
      screenHeight - topMargin,
      categories,
      levelPrecision,
      fixedBounds,
    );

    return bounds;
  }, [
    screenWidth,
    screenHeight,
    topMargin,
    categories,
    levelPrecision,
    fixedBounds,
  ]);

  // Calculate quadrant boundaries
  const quadrantBounds = useMemo(() => {
    const gridPadding = 40;
    const availableWidth = screenWidth - gridPadding * 2;
    const availableHeight = screenHeight - topMargin - gridPadding * 2;

    const quadrantWidth = availableWidth / 2;
    const quadrantHeight = availableHeight / 2;

    return {
      gridPadding,
      availableWidth,
      availableHeight,
      quadrantWidth,
      quadrantHeight,
      // Quadrant positions
      topLeft: {
        x: gridPadding,
        y: gridPadding,
        w: quadrantWidth,
        h: quadrantHeight,
      },
      topRight: {
        x: gridPadding + quadrantWidth,
        y: gridPadding,
        w: quadrantWidth,
        h: quadrantHeight,
      },
      bottomLeft: {
        x: gridPadding,
        y: gridPadding + quadrantHeight,
        w: quadrantWidth,
        h: quadrantHeight,
      },
      bottomRight: {
        x: gridPadding + quadrantWidth,
        y: gridPadding + quadrantHeight,
        w: quadrantWidth,
        h: quadrantHeight,
      },
    };
  }, [screenWidth, screenHeight, topMargin]);

  return (
    <>
      {/* Grid Lines */}
      <div
        style={{
          pointerEvents: "none",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        {/* Quadrant boundary lines */}
        {/* Vertical center line */}
        <div
          style={{
            position: "absolute",
            left: quadrantBounds.gridPadding + quadrantBounds.quadrantWidth,
            top: topMargin + quadrantBounds.gridPadding,
            width: "2px",
            height: quadrantBounds.availableHeight,
            backgroundColor: "#ffffff",
            zIndex: 2,
            opacity: portalOpen ? 0.5 : 1,
            transition: "opacity 0.3s ease-in-out",
          }}
        />
      </div>

      {/* Horizontal center line */}
      <div
        style={{
          position: "absolute",
          left: quadrantBounds.gridPadding,
          top:
            topMargin +
            quadrantBounds.gridPadding +
            quadrantBounds.quadrantHeight,
          width: quadrantBounds.availableWidth,
          height: "2px",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          zIndex: 3,
          opacity: portalOpen ? 0.5 : 1,
          transition: "opacity 0.3s ease-in-out",
        }}
      />

      {/* X-axis labels */}
      <Grid
        container
        style={{
          position: "absolute",
          left: 0,
          top: topMargin + quadrantBounds.availableHeight / 2 - 25,
          zIndex: 4,
          width: "100%",
          padding: 10,
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="overline"
          style={{
            color: "#ffffff",
            fontSize: 18,
            fontWeight: "bold",
          }}
        >
          {adultMode ? "Deficit" : "Hungry"}
        </Typography>
        <Typography
          variant="overline"
          style={{
            color: "#ffffff",
            fontSize: 18,
            fontWeight: "bold",
          }}
        >
          {adultMode ? "Surplus" : "Stuffed"}
        </Typography>
      </Grid>

      {/* Y-axis labels*/}
      <Grid
        container
        style={{
          position: "absolute",
          top: topMargin,
          zIndex: 4,
          justifyContent: "space-between",
          flexDirection: "column",
          height: screenHeight - topMargin,
          width: "100%",
          alignItems: "center",
        }}
      >
        <Typography
          variant="overline"
          style={{
            color: "#ffffff",
            fontSize: 18,
            fontWeight: "bold",
            lineHeight: "revert",
          }}
        >
          {adultMode ? "Needs" : "Needs"}
          {budgetPercentages?.needsPercentage !== null && (
            <span style={{ fontSize: "14px", marginLeft: "4px" }}>
              - {budgetPercentages.needsPercentage}%
            </span>
          )}
        </Typography>
        <Typography
          variant="overline"
          style={{
            color: "#ffffff",
            fontSize: 18,
            fontWeight: "bold",
            lineHeight: "revert",
          }}
        >
          {adultMode ? "Wants" : "Wants"}
          {budgetPercentages?.wantsPercentage !== null && (
            <span style={{ fontSize: "14px", marginLeft: "4px" }}>
              - {budgetPercentages.wantsPercentage}%
            </span>
          )}
        </Typography>
      </Grid>

      {/* Cat Boxes */}
      {categories.map((category, index) => {
        if (hiddenCatIndices?.has(index)) return null;
        const surplus = category.budgeted - category.spent;
        const isBalanced = surplus === 0;

        // Determine the base bounds for this cat
        const bounds = coordinateBounds[index];

        if (!bounds) return null; // safety – should always exist

        const { w: boxW, h: boxH } = bounds;

        let position;
        // Use live cat positions if available, otherwise compute from bounds / balanced logic
        const livePosition = catPositions?.[index];

        if (livePosition) {
          // Box follows Phaser-reported center but keeps the same size as in bounds
          position = {
            x: livePosition.x - boxW / 2,
            y: livePosition.y - boxH / 2,
            width: boxW,
            height: boxH,
          };
        } else if (isBalanced) {
          // Balanced cats stay in the screen centre but still use their allocated size
          const centerX = screenWidth / 2;
          const centerY = topMargin + (screenHeight - topMargin) / 2;

          position = {
            x: centerX - boxW / 2,
            y: centerY - boxH / 2,
            width: boxW,
            height: boxH,
          };
        } else {
          // Default: use calculated quadrant center
          const center = {
            x: bounds.x + bounds.w / 2,
            y: bounds.y + bounds.h / 2,
          };

          position = {
            x: center.x - bounds.w / 2,
            y: topMargin + center.y - bounds.h / 2,
            width: bounds.w,
            height: bounds.h,
          };
        }

        const isSelected = selectedCategoryIndex === index;
        const isReceivingTransfer = transferAnimation?.to === index;

        if (onCategoryClick) {
          return (
            <InteractiveCatBox
              key={`category-${index}-${category.name}`}
              category={category}
              position={position}
              adultMode={adultMode}
              catIndex={index}
              isSelected={isSelected}
              isReceivingTransfer={isReceivingTransfer}
              onCatClick={() => onCategoryClick(index)}
              budgetPercentages={budgetPercentages}
            />
          );
        } else {
          return (
            <CatBox
              key={`category-${index}-${category.name}`}
              category={category}
              position={position}
              adultMode={adultMode}
              catIndex={index}
              selected={isSelected}
              budgetPercentages={budgetPercentages}
            />
          );
        }
      })}

      {/* Debug coordinate plane overlay */}
      {SHOW_DEBUG && (
        <div style={{ pointerEvents: "none" }}>
          {/* React screen boundary - removed duplicate, keeping Phaser one */}
          <div
            style={{
              position: "absolute",
              left: 10,
              top: 30, // Moved down to avoid overlap with top margin label
              color: "purple",
              fontSize: "16px",
              fontWeight: "bold",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              padding: "4px 8px",
              zIndex: 997,
            }}
          >
            React: {screenWidth}x{screenHeight}
          </div>

          {/* Top margin boundary */}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: topMargin,
              width: screenWidth,
              height: 2,
              backgroundColor: "orange",
              zIndex: 997,
            }}
          />
          <div
            style={{
              position: "absolute",
              left: 10,
              top: 10, // Moved to top so it's always visible
              color: "orange",
              fontSize: "14px",
              fontWeight: "bold",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              padding: "2px 6px",
              zIndex: 997,
            }}
          >
            Top Margin: {topMargin}px
          </div>

          {/* Coordinate bounds */}
          {coordinateBounds.map((bounds, index) => (
            <div
              key={`debug-${index}`}
              style={{
                position: "absolute",
                left: bounds.x,
                top: topMargin + bounds.y,
                width: bounds.w,
                height: bounds.h,
                border: "3px solid red",
                backgroundColor: "rgba(255, 0, 0, 0.1)",
                zIndex: 998,
              }}
            />
          ))}
        </div>
      )}
    </>
  );
};
